import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Modal from "@material-ui/core/Modal";
import { Document, Page, pdf, StyleSheet, Text, View } from "@react-pdf/renderer";
import React, { useEffect, useState } from "react";
import { GrDocumentPdf } from "react-icons/gr";
import { LuX } from "react-icons/lu";

import { CARGO_CSV_COLUMNS } from "./aiImportSchema";

const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 50,
    paddingHorizontal: 40,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
  },
  headerFixed: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
  },
  footerFixed: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: "center",
  },
  body: {
    marginTop: 40,
    marginBottom: 40,
  },
  section: {
    marginBottom: 20,
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#313C4C",
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#313C4C",
    paddingBottom: 4,
  },
  heading1: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#313C4C",
    marginTop: 15,
    marginBottom: 8,
  },
  heading2: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#333333",
    marginTop: 10,
    marginBottom: 6,
  },
  paragraph: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#444444",
    marginBottom: 8,
  },
  bulletList: {
    marginLeft: 10,
    marginBottom: 10,
  },
  bulletItem: {
    flexDirection: "row",
    marginBottom: 4,
  },
  bulletPoint: {
    width: 15,
    fontSize: 10,
    color: "#313C4C",
  },
  bulletText: {
    flex: 1,
    fontSize: 10,
    lineHeight: 1.4,
    color: "#444444",
  },
  numberedItem: {
    flexDirection: "row",
    marginBottom: 6,
  },
  numberedPoint: {
    width: 20,
    fontSize: 10,
    fontWeight: "bold",
    color: "#313C4C",
  },
  infoBox: {
    backgroundColor: "#e8f4fd",
    borderLeftWidth: 4,
    borderLeftColor: "#313C4C",
    padding: 10,
    marginBottom: 12,
    borderRadius: 2,
  },
  warningBox: {
    backgroundColor: "#fff4e5",
    borderLeftWidth: 4,
    borderLeftColor: "#ff9800",
    padding: 10,
    marginBottom: 12,
    borderRadius: 2,
  },
  promptBox: {
    backgroundColor: "#1e1e1e",
    padding: 15,
    borderRadius: 6,
    marginBottom: 15,
  },
  promptText: {
    color: "#4caf50",
    fontFamily: "Courier",
    fontSize: 10,
    lineHeight: 1.5,
  },
  schemaContainer: {
    backgroundColor: "#f8f9fa",
    borderWidth: 1,
    borderColor: "#e9ecef",
    borderRadius: 4,
    padding: 10,
    marginTop: 10,
  },
  schemaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 2,
  },
  schemaKey: {
    fontFamily: "Courier",
    fontSize: 9,
    color: "#d63384",
    fontWeight: "bold",
  },
  schemaValue: {
    fontFamily: "Courier",
    fontSize: 9,
    color: "#0d6efd",
  },
  schemaType: {
    fontFamily: "Courier",
    fontSize: 8,
    color: "#6c757d",
    marginLeft: 4,
    fontStyle: "italic",
  },
  schemaBracket: {
    fontFamily: "Courier",
    fontSize: 9,
    color: "#333333",
  },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "#bfbfbf",
    marginBottom: 15,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableColHeader: {
    width: "50%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "#bfbfbf",
    backgroundColor: "#f2f2f2",
  },
  tableCol: {
    width: "50%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "#bfbfbf",
  },
  tableCellHeader: {
    margin: 5,
    fontSize: 10,
    fontWeight: "bold",
  },
  tableCell: {
    margin: 5,
    fontSize: 9,
  },
});

const SectionTitle = ({ children }) => <Text style={styles.heading1}>{children}</Text>;
const SubTitle = ({ children }) => <Text style={styles.heading2}>{children}</Text>;
const Paragraph = ({ children }) => <Text style={styles.paragraph}>{children}</Text>;

const BulletList = ({ items }) => (
  <View style={styles.bulletList}>
    {items.map((item, index) => (
      <View key={index} style={styles.bulletItem}>
        <Text style={styles.bulletPoint}>•</Text>
        <Text style={styles.bulletText}>{item}</Text>
      </View>
    ))}
  </View>
);

const NumberedList = ({ items }) => (
  <View style={styles.bulletList}>
    {items.map((item, index) => (
      <View key={index} style={styles.numberedItem}>
        <Text style={styles.numberedPoint}>{index + 1}.</Text>
        <Text style={styles.bulletText}>{item}</Text>
      </View>
    ))}
  </View>
);

const InfoBox = ({ title, children }) => (
  <View style={styles.infoBox}>
    {title && <Text style={{ ...styles.heading2, marginTop: 0, color: "#313C4C" }}>{title}</Text>}
    <Text style={styles.paragraph}>{children}</Text>
  </View>
);

const WarningBox = ({ title, children }) => (
  <View style={styles.warningBox}>
    {title && <Text style={{ ...styles.heading2, marginTop: 0, color: "#d84315" }}>{title}</Text>}
    <Text style={styles.paragraph}>{children}</Text>
  </View>
);

const TableRow = ({ left, right, isHeader = false }) => (
  <View style={styles.tableRow}>
    <View style={isHeader ? styles.tableColHeader : styles.tableCol}>
      <Text style={isHeader ? styles.tableCellHeader : styles.tableCell}>{left}</Text>
    </View>
    <View style={isHeader ? styles.tableColHeader : styles.tableCol}>
      <Text style={isHeader ? styles.tableCellHeader : styles.tableCell}>{right}</Text>
    </View>
  </View>
);

const AIInstructionDocument = () => (
  <Document>
    <Page size="A4" style={styles.page} wrap>

      <View style={styles.body}>
        <Text style={styles.title}>AI Cargo Extraction Master Protocol</Text>

        <SectionTitle>1. Purpose & Overview</SectionTitle>
        <Paragraph>
          This document provides absolute, strict instructions for Large Language Models (LLMs) such as
          ChatGPT, Claude, Gemini, or Grok. Its purpose is to guide the AI in extracting complex logistics,
          shipping, and cargo details from unstructured client files (PDFs, DOCX, emails, scanned images)
          and formatting them into a highly standardized CSV file for direct system import.
        </Paragraph>

        <SectionTitle>2. Accepted Inputs & Supported Models</SectionTitle>
        <InfoBox title="Model Compatibility">
          These instructions are optimized for models with code-execution or advanced data analysis features
          (e.g., ChatGPT Plus, Claude 3.5 Sonnet). Provide this exact document along with the unstructured payload.
        </InfoBox>
        <Paragraph>
          You (the user) should upload this PDF as the "System Instruction" or primary context file,
          followed immediately by the documents containing the actual cargo data.
        </Paragraph>
        <SubTitle>Supported Document Types for Payload:</SubTitle>
        <BulletList items={[
          "Commercial Invoices & Packing Lists",
          "Bills of Lading (B/L) & Air Waybills (AWB)",
          "Arrival Notices & Delivery Orders",
          "Raw email threads communicating shipment details",
          "Scanned OCR outputs or unstructured raw text"
        ]} />

        <SectionTitle>3. The Extraction Workflow</SectionTitle>
        <NumberedList items={[
          "Read and comprehend this entire instruction manual.",
          "Analyze the attached payload documents thoroughly.",
          "Identify all relevant entities (Shipper, Consignee, Cargo Weight, Dimensions, Port of Loading, etc.).",
          "Apply the strict validation and formatting rules defined on Page 2.",
          "Map the extracted data precisely to the CSV columns defined in Section 6.",
          "Use code execution tools to generate a downloadable .csv file."
        ]} />

        <View break />

        <SectionTitle>4. Detailed AI Responsibilities & Rules</SectionTitle>
        <Paragraph>
          The AI MUST adhere strictly to the following extraction, validation, and formatting rules.
          Deviation will result in automated system import failures.
        </Paragraph>

        <SubTitle>Extraction Rules</SubTitle>
        <BulletList items={[
          "Extract EVERY possible detail that matches the schema.",
          "If a value is ambiguous, infer the most likely intended value based on standard shipping context.",
          "Ignore extraneous conversational text, marketing material, or boilerplate terms and conditions.",
          "Maintain maximum precision for weights, volumes, and monetary values."
        ]} />

        <SubTitle>Validation & Formatting Rules</SubTitle>
        <View style={styles.table}>
          <TableRow isHeader left="Data Type" right="Formatting Requirement" />
          <TableRow left="Dates & Times" right="Must be strictly ISO 8601 format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ssZ)." />
          <TableRow left="Numbers & Currency" right="Float or Integer only. Remove ALL commas, currency symbols, and text. (e.g., '1,234.56 USD' -> 1234.56)" />
          <TableRow left="Text & Strings" right="Trim whitespace and line breaks. Strip redundant words like 'Port of' or 'Port' from location names (e.g. 'PortMiami' -> 'Miami')." />
          <TableRow left="Unknown Values" right="Leave the CSV cell empty. Do not write 'null', 'N/A', 'Unknown' or 0 for unknown values." />
        </View>

        <SubTitle>Unit Conversion Rules</SubTitle>
        <WarningBox title="Critical Unit Integrity">
          Do not blindly convert units unless the target schema field name explicitly demands a specific unit
          (e.g., if the field is `weightKg` but the document says `500 lbs`, output `226.796`).
          If the schema asks for a generic `weight` and a `weightUnit` field separately, preserve the original values.
        </WarningBox>

        <SubTitle>Handling Enums & Standardized Codes</SubTitle>
        <BulletList items={[
          "Incoterms: Must be 3 uppercase letters (e.g., FOB, CIF, EXW).",
          "Currency: Must be standard 3-letter ISO 4217 codes (e.g., USD, EUR, GBP).",
          "Country Codes: Use 2-letter ISO 3166-1 alpha-2 codes (e.g., US, IN, CN).",
          "UN/LOCODE: Use the standard 5-character port codes if identifiable."
        ]} />

        <View break />

        <SectionTitle>5. Output Strictness & File Generation Prompt</SectionTitle>

        <WarningBox title="File Download Required">
          The downstream system requires a physical file to be uploaded. The AI must NOT output the raw CSV
          as text in the chat window. It MUST use its internal code execution capabilities (like Python or
          Advanced Data Analysis) to generate a `.csv` file and provide a download link.
        </WarningBox>

        <SubTitle>Common AI Hallucinations to Avoid</SubTitle>
        <BulletList items={[
          "Printing the CSV as text instead of generating a file.",
          "Inventing tracking numbers or vessel names that do not exist in the source document.",
          "Merging port operations into one row, or changing/renaming the header columns.",
          "Adding custom columns that were not requested."
        ]} />

        <SubTitle>Handling Missing or Insufficient Cargo Data</SubTitle>
        <BulletList items={[
          "While some empty fields are acceptable, the document must contain a minimum threshold of cargo details (specifically, a cargo name/description or at least one port operation with a port name).",
          "If the document does not contain actual cargo booking or voyage requirement data, or the data is insufficient to populate the schema meaningfully, you MUST NOT hallucinate, invent values, or attempt to fit unrelated data into the cargo schema.",
          "In these cases, do NOT generate any CSV file. Instead, simply reply with a normal text message explaining that no valid cargo details were found in the document."
        ]} />

        <SubTitle>The Ultimate System Prompt</SubTitle>
        <Paragraph>
          Users should copy and paste the following prompt exactly when interacting with the AI:
        </Paragraph>

        <View style={styles.promptBox}>
          <Text style={styles.promptText}>
            {`"You are an expert logistics data extraction system. I am providing you with unstructured shipping documents and a CSV column specification. Your task is to extract all relevant cargo and shipment data from the documents and map it perfectly to the columns.

Use exactly the header row from the specification, in the same order. Write ONE ROW PER PORT OPERATION (Loading or Discharging). Fill the cargo-level columns (name, freightMode, qtyMode, laycanStartDate, laycanEndDate, suez, panama, message) on the FIRST row only and leave them empty on later rows. Wrap any value containing a comma, quote or line break in double quotes. Apply all date ISO conversion, number sanitization, and enum standardization rules. Leave unknown cells empty.

CRITICAL REQUIREMENT: Do NOT output the CSV text in the chat window. You MUST use your Advanced Data Analysis / Python code execution environment to save the result as a UTF-8 file named \`extracted_cargo_data.csv\` and provide me with the direct download link.

CRITICAL HANDLING FOR MISSING DATA: If the provided documents do not contain actual cargo booking or voyage requirement data that can meaningfully populate the columns (specifically, lacking both a cargo name/description and any loading/discharging port operations), you MUST NOT generate any CSV file, hallucinate values, or create alternative column layouts. Instead, simply reply with a normal text message in the chat explaining that no valid cargo details were found."`}
          </Text>
        </View>

        <View break />

        <SectionTitle>6. Master Cargo CSV Columns</SectionTitle>
        <Paragraph>
          The first row of the CSV MUST be this header, with these exact column names in this exact order.
          Each following row is one port operation. Example: header, then a Loading row, then a Discharging row.
        </Paragraph>

        <View style={styles.table}>
          <TableRow isHeader left="Column" right="Value" />
          {CARGO_CSV_COLUMNS.map(([name, desc]) => (
            <TableRow key={name} left={name} right={desc} />
          ))}
        </View>

      </View>
    </Page>
  </Document>
);

const DownloadAIInstructionPdf = () => {
  const [open, setOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);

  const handleOpen = () => {
    setDownloadStarted(false);
    setOpen(true);
  };
  const handleClose = () => {
    if (!isDownloading) {
      setOpen(false);
    }
  };

  const executeDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    setError(null);

    let objectUrl = null;

    try {
      const pdfBlob = await pdf(<AIInstructionDocument />).toBlob();
      objectUrl = window.URL.createObjectURL(new Blob([pdfBlob], { type: "application/pdf" }));

      const linkElement = document.createElement("a");
      linkElement.href = objectUrl;
      linkElement.setAttribute("download", `AI_Cargo_Import_Instructions_${Date.now()}.pdf`);
      linkElement.style.display = "none";

      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);
      setDownloadStarted(true);

    } catch (err) {
      console.error("PDF Generation Error:", err);
      setError("Failed to generate PDF. Please try again.");
    } finally {
      if (objectUrl) {
        setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
      }
      setIsDownloading(false);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "calc(100% - 32px)",
    maxWidth: "620px",
    maxHeight: "calc(100vh - 32px)",
    backgroundColor: "#ffffff",
    boxShadow: "0px 10px 30px rgba(0,0,0,0.2)",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    borderRadius: "2px",
    overflow: "hidden",
    outline: "none",
  };

  return (
    <React.Fragment>
      <button
        type="button"
        onClick={handleOpen}
        data-spotlight="export-ai-pdf-btn"
        className="flex items-center gap-1 px-2 py-0.5 h-[24px] text-[11px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-none leading-none cursor-pointer hover:bg-slate-50 hover:text-[#313C4C] transition-colors whitespace-nowrap"
        disabled={isDownloading}
      >
        <GrDocumentPdf size={13} className={isDownloading ? "opacity-50" : ""} />
        {isDownloading ? "Generating..." : "Export AI Instruction PDF"}
      </button>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="ai-instruction-title"
        disableEscapeKeyDown={isDownloading}
        disableBackdropClick={isDownloading}
      >
        <Box style={modalStyle}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-[#f8fafc]">
            <div>
              <h2 id="ai-instruction-title" className="text-base font-semibold text-[#313C4C]">
                Export AI Instruction PDF
              </h2>
              <p className="text-xs text-slate-600 mt-1">
                Use this guide to create a cargo CSV file from your documents.
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={isDownloading}
              aria-label="Close AI instruction guide"
              className={`p-2 rounded-full transition-colors ${isDownloading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'}`}
            >
              <LuX className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-0 overflow-y-auto px-4 py-4 text-[12px] text-slate-700">
            <div className="border border-slate-200 bg-slate-50 px-3 py-3">
              <div className="font-semibold text-slate-900 mb-1">What is this PDF?</div>
              <p>It tells an AI assistant how to read your cargo documents and create a CSV file in the format this app can import. The PDF itself does not contain your cargo details.</p>
            </div>
            <div className="font-semibold text-slate-900 mt-4 mb-2">How to use it</div>
            <ol className="list-decimal pl-5 space-y-2">
              <li>Download the instruction PDF using the button below.</li>
              <li>Give the PDF and your cargo source documents, such as a bill of lading or invoice, to an AI assistant that can create downloadable files.</li>
              <li>Download the CSV file the assistant creates. In the New Cargo form, choose <strong>Upload PDF or CSV</strong> and select that CSV file.</li>
            </ol>
            <p className="mt-3 text-slate-600">The instruction PDF is for the AI assistant; upload its resulting CSV file to the cargo form.</p>
            {downloadStarted && <p className="mt-3 text-emerald-700" role="status">Your PDF download has started.</p>}
            {error && <p className="mt-3 text-rose-700" role="alert">{error}</p>}
          </div>

          <div className="flex justify-end px-4 py-3 border-t border-slate-200 bg-white">
            <div className="flex gap-2">
              <Button
                onClick={handleClose}
                variant="outlined"
                disabled={isDownloading}
                style={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#d1d5db",
                  color: "#374151"
                }}
              >
                Cancel
              </Button>
              <button
                type="button"
                onClick={executeDownload}
                disabled={isDownloading}
                className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-white rounded shadow transition-colors ${isDownloading ? "bg-[#9ca3af] cursor-not-allowed" : "bg-[#313C4C] hover:bg-[#0c2f50]"
                  }`}
              >
                {isDownloading ? (
                  <CircularProgress size={14} style={{ color: "white" }} />
                ) : (
                  <GrDocumentPdf size={14} style={{ filter: "brightness(0) invert(1)" }} />
                )}
                {isDownloading ? "Generating PDF..." : "Download instruction PDF"}
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </React.Fragment>
  );
};

export default DownloadAIInstructionPdf;
